<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import CheckBoxDefault from '@/components/forms/Checkboxes/CheckBoxDefault.vue'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import AgentAssignmentModal from '../Modals/AgentAssignmentModal.vue'
import RemoveFromWatchlistModal from '../Modals/RemoveFromWatchlistModal.vue'
import { EyeIcon, BookMarkIcon, AgentIcon, RefreshIcon, ExternalIcon, TrashIcon } from '@/icons'
import {
  getBadgeStatus,
  getWorkflowLabel
} from '@/utils/badgeUtils'
import router from '@/router'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import { notificationService } from '@/services/notificationService'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

// Extend Tender type to include selected property
interface TenderWithSelection extends Tender {
  selected?: boolean
}

const tenders = ref<TenderWithSelection[]>([])
const searchFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)
const expiredTendersCount = ref(0)
const removingFromWatchlist = ref(false)
const selectedTenders = ref<TenderWithSelection[]>([])

// Modal loading states
const assigningAgent = ref(false)
const removingFromWatchlistSingle = ref(false)

// Add new state variables for individual tender removal and agent assignment
const showRemoveModal = ref(false)
const tenderToRemove = ref<TenderWithSelection | null>(null)

const isAgentAssignmentModalOpen = ref(false)
const tenderToAssign = ref<TenderWithSelection | null>(null)
const agentAssignmentModalRef = ref<InstanceType<typeof AgentAssignmentModal> | null>(null)

// Bulk action modal states
const showBulkRemoveModal = ref(false)
const showBulkAssignModal = ref(false)
const selectedTendersForBulkRemoval = ref<TenderWithSelection[]>([])
const selectedTendersForBulkAssignment = ref<TenderWithSelection[]>([])
const bulkAgentAssignmentModalRef = ref<InstanceType<typeof AgentAssignmentModal> | null>(null)

// Computed
const allSelected = computed({
  get: () => tenders.value.length > 0 && tenders.value.every(t => t.selected === true),
  set: (value: boolean) => {
    tenders.value.forEach(tender => {
      tender.selected = value
    })
    selectedTenders.value = value ? [...tenders.value] : []
  }
})

const hasSelectedTenders = computed(() => selectedTenders.value.length > 0)

// Fetch watchlist tenders
const fetchWatchlistTenders = async () => {
  try {
    loading.value = true
    error.value = null

    const response = await api.get('/api/tenders/watchlist')

    const watchlistData = response.data

    if (watchlistData.success && Array.isArray(watchlistData.tenders)) {
      tenders.value = watchlistData.tenders.map((tender: Tender) => ({
        ...tender,
        selected: false
      }))
      selectedTenders.value = []
      console.log(`✅ Loaded ${tenders.value.length} watchlist tenders`)
      await fetchExpiredCount()
    } else {
      tenders.value = []
      console.log('⚠️ No watchlist tenders found or invalid response format')
    }
  } catch (err) {
    console.error('Error loading watchlist tenders:', err)
    error.value = getAxiosErrorMessage(err)
    notificationService.error(`Failed to load watchlist tenders: ${getAxiosErrorMessage(err)}`)
  } finally {
    loading.value = false
  }
}

// Fetch expired watchlist count
const fetchExpiredCount = async () => {
  try {
    const response = await api.get('/api/tenders/watchlist/count')
    if (response.data && response.data.count !== undefined) {
      expiredTendersCount.value = response.data.count
    } else {
      const expiredCount = tenders.value.filter(t => {
        if (!t.deadline) return false
        try {
          const deadline = new Date(t.deadline)
          const now = new Date()
          return deadline < now
        } catch {
          return false
        }
      }).length
      expiredTendersCount.value = expiredCount
    }
  } catch (err) {
    console.error('Error fetching expired count:', err)
  }
}

// Filtered tenders based on search
const filteredTenders = computed(() => {
  if (!searchFilter.value) return tenders.value

  const searchTerm = searchFilter.value.toLowerCase()
  return tenders.value.filter(
    (tender) =>
      tender.t247Id?.toLowerCase().includes(searchTerm) ||
      tender.referenceNo?.toLowerCase().includes(searchTerm) ||
      tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
      tender.organization?.toLowerCase().includes(searchTerm) ||
      tender.location?.toLowerCase().includes(searchTerm),
  )
})

// Check if tender is expired
const isTenderExpired = (tender: Tender): boolean => {
  if (!tender.deadline) return false
  try {
    const deadline = new Date(tender.deadline)
    const now = new Date()
    return deadline < now
  } catch {
    return false
  }
}

const handleCheckboxChange = (tender: TenderWithSelection, isChecked: boolean) => {
  tender.selected = isChecked
  if (isChecked) {
    selectedTenders.value.push(tender)
  } else {
    selectedTenders.value = selectedTenders.value.filter(t => t.t247Id !== tender.t247Id)
  }
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'No deadline'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

const getStatusLabel = (status: Tender['status']): string => {
  return getWorkflowLabel(status)
}

const getStatusBadgeClass = (status: Tender['status']): string => {
  return 'p-badge-' + getBadgeStatus(status)
}

const getExpiryStatus = (tender: Tender): { label: string; class: string } => {
  if (!tender.deadline) return { label: 'No deadline', class: 'text-gray-600' }

  if (isTenderExpired(tender)) {
    return { label: 'Expired', class: 'text-red-600 font-medium' }
  }

  try {
    const now = new Date()
    const deadline = new Date(tender.deadline)
    const diffDays = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < 7) {
      return { label: `${diffDays} days left`, class: 'text-orange-600' }
    }
    return { label: `${diffDays} days left`, class: 'text-green-600' }
  } catch {
    return { label: 'Invalid date', class: 'text-gray-600' }
  }
}

const viewTenderDetails = (tender: Tender) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'watchlist-tenders' },
  })
}

// Open remove modal for a single tender
const openRemoveModal = (tender: TenderWithSelection) => {
  tenderToRemove.value = tender
  showRemoveModal.value = true
}

// Close remove modal
const closeRemoveModal = () => {
  showRemoveModal.value = false
  tenderToRemove.value = null
}

// Handle remove from watchlist (called from modal)
const handleRemoveFromWatchlist = async () => {
  if (!tenderToRemove.value) return

  removingFromWatchlistSingle.value = true
  try {
    const tenderId = tenderToRemove.value.t247Id

    const response = await api.put(`/api/tenders/${tenderId}/watchlist`, {
      isWatchlisted: false
    })

    if (response.data.success) {
      tenders.value = tenders.value.filter((t) => t.t247Id !== tenderId)
      selectedTenders.value = selectedTenders.value.filter((t) => t.t247Id !== tenderId)
      await fetchExpiredCount()
      notificationService.success(`Tender removed from watchlist successfully!`)
      closeRemoveModal()
    }
  } catch (err) {
    console.error('Error removing from watchlist:', err)
    notificationService.error(`Failed to remove from watchlist: ${getAxiosErrorMessage(err)}`)
  } finally {
    removingFromWatchlistSingle.value = false
  }
}

// Open bulk remove modal - NO FILTERING, move all selected tenders
const openBulkRemoveModal = () => {
  if (selectedTenders.value.length === 0) {
    notificationService.warning('Please select tenders first')
    return
  }
  // Move ALL selected tenders without any filtering
  selectedTendersForBulkRemoval.value = [...selectedTenders.value]
  showBulkRemoveModal.value = true
}

const closeBulkRemoveModal = () => {
  showBulkRemoveModal.value = false
  selectedTendersForBulkRemoval.value = []
}

const handleBulkRemove = async () => {
  removingFromWatchlist.value = true
  try {
    const tenderIds = selectedTendersForBulkRemoval.value.map(t => t.t247Id)

    const response = await api.post('/api/tenders/watchlist/bulk', {
      tenderIds: tenderIds,
      isWatchlisted: false
    })

    if (response.data.success) {
      tenders.value = tenders.value.filter(t => !tenderIds.includes(t.t247Id))
      selectedTenders.value = selectedTenders.value.filter(t => !tenderIds.includes(t.t247Id))
      await fetchExpiredCount()
      notificationService.success(`Successfully removed ${tenderIds.length} tender(s) from watchlist.`)
      closeBulkRemoveModal()
    } else {
      throw new Error(response.data.error || 'Bulk removal failed')
    }
  } catch (err) {
    console.error('Error removing tenders:', err)
    notificationService.error(`Failed to remove tenders: ${getAxiosErrorMessage(err)}`)
  } finally {
    removingFromWatchlist.value = false
  }
}

// Open agent assignment modal for single tender
const openAgentAssignmentModal = (tender: TenderWithSelection) => {
  tenderToAssign.value = tender
  isAgentAssignmentModalOpen.value = true
}

// Open bulk agent assignment modal - NO FILTERING, move all selected tenders
const openBulkAgentAssignmentModal = () => {
  if (selectedTenders.value.length === 0) {
    notificationService.warning('Please select tenders first')
    return
  }
  // Move ALL selected tenders without any filtering
  selectedTendersForBulkAssignment.value = [...selectedTenders.value]
  showBulkAssignModal.value = true
}

const closeBulkAssignModal = () => {
  showBulkAssignModal.value = false
  selectedTendersForBulkAssignment.value = []
}

// Handle agent assignment from watchlist (single)
const handleAgentAssignment = async (assignmentData: {
  tenderId: string
  agentId: string
  notes: string
  agentName?: string
}) => {
  assigningAgent.value = true
  try {
    console.log('🔄 Assigning agent to watchlist tender:', assignmentData)

    let agentName = assignmentData.agentName
    if (!agentName) {
      try {
        const agentResponse = await api.get(`/api/users/agents/${assignmentData.agentId}`)
        if (agentResponse.data && agentResponse.data.name) {
          agentName = agentResponse.data.name
        }
      } catch (agentError) {
        console.warn('Could not fetch agent name:', agentError)
        agentName = 'Unknown Agent'
      }
    }

    const response = await api.put('/api/tenders/assign-agent', {
      tenderId: assignmentData.tenderId,
      agentId: assignmentData.agentId,
      agentName: agentName,
      notes: assignmentData.notes,
      status: 'assigned-to-agent',
      workflowStatus: 'assigned-to-agent',
      assignedAt: new Date().toISOString(),
    })

    console.log('✅ Assignment response:', response.data)

    if (response.data.success) {
      tenders.value = tenders.value.filter((t) => t.t247Id !== assignmentData.tenderId)
      selectedTenders.value = selectedTenders.value.filter((t) => t.t247Id !== assignmentData.tenderId)
      await fetchExpiredCount()

      notificationService.success(
        `Agent "${agentName}" assigned successfully! Tender moved to workflow.`,
      )
      isAgentAssignmentModalOpen.value = false
    } else {
      throw new Error(response.data.error || 'Assignment failed')
    }
  } catch (error) {
    console.error('❌ Assignment error:', error)

    try {
      console.log('🔄 Trying two-step assignment...')

      await api.put('/api/tenders/update-status', {
        tenderIds: [assignmentData.tenderId],
        status: 'assigned-to-agent',
        workflowStatus: 'assigned-to-agent',
      })

      await api.put('/api/tenders/assign-agent', {
        tenderId: assignmentData.tenderId,
        agentId: assignmentData.agentId,
        notes: assignmentData.notes,
        status: 'assigned-to-agent',
        workflowStatus: 'assigned-to-agent',
        assignedAt: new Date().toISOString(),
      })

      tenders.value = tenders.value.filter((t) => t.t247Id !== assignmentData.tenderId)
      selectedTenders.value = selectedTenders.value.filter((t) => t.t247Id !== assignmentData.tenderId)
      await fetchExpiredCount()

      notificationService.success('Agent assigned successfully! Tender moved to workflow.')
      isAgentAssignmentModalOpen.value = false
    } catch (fallbackError) {
      console.error('❌ Fallback assignment also failed:', fallbackError)
      notificationService.error(`Failed to assign agent: ${getAxiosErrorMessage(error)}`)
    }
  } finally {
    assigningAgent.value = false
  }
}

// Handle bulk agent assignment - NO FILTERING
const handleBulkAgentAssignment = async (assignmentData: {
  tenderId: string
  agentId: string
  agentName: string
  notes: string
}) => {
  assigningAgent.value = true
  try {
    let successCount = 0
    let failCount = 0
    const failedTenders: string[] = []

    for (const tender of selectedTendersForBulkAssignment.value) {
      try {
        const response = await api.put('/api/tenders/assign-agent', {
          tenderId: tender.t247Id,
          agentId: assignmentData.agentId,
          agentName: assignmentData.agentName,
          notes: assignmentData.notes,
          status: 'assigned-to-agent',
          workflowStatus: 'assigned-to-agent',
          assignedAt: new Date().toISOString()
        })

        if (response.data.success) {
          successCount++
        } else {
          failCount++
          failedTenders.push(tender.referenceNo || tender.t247Id)
        }
      } catch (error) {
        console.error(`Error assigning tender ${tender.t247Id} to agent:`, error)
        failCount++
        failedTenders.push(tender.referenceNo || tender.t247Id)
      }
    }

    const removedTenderIds = selectedTendersForBulkAssignment.value.map(t => t.t247Id)
    tenders.value = tenders.value.filter(t => !removedTenderIds.includes(t.t247Id))
    selectedTenders.value = selectedTenders.value.filter(t => !removedTenderIds.includes(t.t247Id))
    await fetchExpiredCount()

    if (successCount > 0) {
      if (successCount === selectedTendersForBulkAssignment.value.length) {
        notificationService.success(
          `✅ All ${successCount} tender(s) assigned to ${assignmentData.agentName} successfully!`,
          5000
        )
      } else {
        notificationService.warning(
          `⚠️ ${successCount} tender(s) assigned to ${assignmentData.agentName}, ${failCount} failed.\nFailed: ${failedTenders.join(', ')}`,
          8000
        )
      }
    } else {
      notificationService.error('❌ Failed to assign any tenders to agent')
    }

    showBulkAssignModal.value = false
    selectedTendersForBulkAssignment.value = []
  } catch (error) {
    console.error('Bulk assignment error:', error)
    notificationService.error(`Failed to assign tenders: ${getAxiosErrorMessage(error)}`)
  } finally {
    assigningAgent.value = false
  }
}

// Handle modal assign button click
const handleModalAssign = () => {
  if (agentAssignmentModalRef.value) {
    agentAssignmentModalRef.value.submitForm()
  }
}

// Handle modal bulk assign button click
const handleModalBulkAssign = () => {
  if (bulkAgentAssignmentModalRef.value) {
    bulkAgentAssignmentModalRef.value.submitForm()
  }
}

// Cancel agent assignment
const handleCancelAssignment = () => {
  isAgentAssignmentModalOpen.value = false
  tenderToAssign.value = null
}

const clearFilters = () => {
  searchFilter.value = ''
}

// Tooltip management
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipTarget = ref<HTMLElement>();
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('top');

const showTooltip = (event: MouseEvent, text: string) => {
  const target = event.currentTarget as HTMLElement;
  tooltipTarget.value = target;
  tooltipText.value = text;
  tooltipPosition.value = 'top';
  tooltipVisible.value = true;
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipTarget.value = undefined;
  tooltipText.value = '';
};

onMounted(() => {
  fetchWatchlistTenders()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Watchlist" :icon="BookMarkIcon" />

    <!-- Bulk Remove Confirmation Modal -->
    <ModalDefault
      v-model="showBulkRemoveModal"
      title="Remove from Watchlist (Bulk)"
      width="max-w-md"
      @close="closeBulkRemoveModal"
      :loading="removingFromWatchlist"
    >
      <div class="p-4">
        <div class="flex items-center mb-4">
          <div class="h-6 w-6 text-purple-500 mr-3">
            <BookMarkIcon class="w-6 h-6" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Remove Selected Tenders</h3>
        </div>

        <p class="text-gray-600 mb-4">
          Are you sure you want to remove
          <span class="font-semibold">{{ selectedTendersForBulkRemoval.length }}</span>
          tender(s) from your watchlist? This action cannot be undone.
        </p>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p class="text-sm text-yellow-800">
            <span class="font-medium">Note:</span> All selected tenders will be removed from the watchlist, including expired ones.
          </p>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <BaseButton
            text="Cancel"
            variant="secondary"
            :disabled="removingFromWatchlist"
            @click="closeBulkRemoveModal"
          />
          <BaseButton
            text="Remove Selected"
            variant="warning"
            :icon="TrashIcon"
            :loading="removingFromWatchlist"
            loading-text="Removing..."
            @click="handleBulkRemove"
          />
        </div>
      </template>
    </ModalDefault>

    <!-- Bulk Agent Assignment Modal -->
    <ModalDefault
      v-model="showBulkAssignModal"
      title="Bulk Assign Tenders to Agent"
      width="max-w-lg"
      classBody="my-4"
      @close="closeBulkAssignModal"
      :loading="assigningAgent"
    >
      <AgentAssignmentModal
        v-if="showBulkAssignModal && selectedTendersForBulkAssignment.length > 0"
        ref="bulkAgentAssignmentModalRef"
        :tender="selectedTendersForBulkAssignment[0]"
        :currentAgentId="undefined"
        :loading="assigningAgent"
        @assign="handleBulkAgentAssignment"
        @cancel="closeBulkAssignModal"
      />
      <div v-if="selectedTendersForBulkAssignment.length > 0" class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium text-blue-800">Selected Tenders: {{ selectedTendersForBulkAssignment.length }}</h3>
          <span class="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">Bulk Action</span>
        </div>
        <ul class="space-y-1 text-sm max-h-32 overflow-y-auto">
          <li
            v-for="tender in selectedTendersForBulkAssignment"
            :key="tender.t247Id"
            class="flex justify-between py-1 border-b border-blue-200 last:border-0"
          >
            <span class="text-blue-600">{{ tender.referenceNo }}</span>
            <span class="text-blue-800 font-medium">{{ tender.tenderBrief?.substring(0, 30) }}...</span>
          </li>
        </ul>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <BaseButton
            text="Cancel"
            variant="secondary"
            :disabled="assigningAgent"
            @click="closeBulkAssignModal"
          />
          <BaseButton
            text="Assign to All Selected"
            variant="primary"
            :loading="assigningAgent"
            loading-text="Assigning..."
            @click="handleModalBulkAssign"
          />
        </div>
      </template>
    </ModalDefault>

    <!-- Single Tender Remove Modal -->
    <ModalDefault
      v-model="showRemoveModal"
      title="Remove from Watchlist"
      width="max-w-md"
      @close="closeRemoveModal"
      :loading="removingFromWatchlistSingle"
    >
      <RemoveFromWatchlistModal
        v-if="tenderToRemove"
        :tender="tenderToRemove"
        :loading="removingFromWatchlistSingle"
        @confirm="handleRemoveFromWatchlist"
        @close="closeRemoveModal"
      />
      <template #footer>
        <div class="flex justify-end gap-3">
          <BaseButton
            text="Cancel"
            variant="secondary"
            @click="closeRemoveModal"
            :disabled="removingFromWatchlistSingle"
          />
          <BaseButton
            text="Remove from Watchlist"
            variant="warning"
            :icon="BookMarkIcon"
            :loading="removingFromWatchlistSingle"
            loading-text="Removing..."
            @click="handleRemoveFromWatchlist"
            :disabled="removingFromWatchlistSingle"
          />
        </div>
      </template>
    </ModalDefault>

    <!-- Single Agent Assignment Modal -->
    <ModalDefault
      v-model="isAgentAssignmentModalOpen"
      title="Assign Watchlist Tender to Agent"
      width="max-w-lg"
      classBody="my-4"
      :loading="assigningAgent"
    >
      <AgentAssignmentModal
        v-if="tenderToAssign"
        ref="agentAssignmentModalRef"
        :tender="tenderToAssign"
        :currentAgentId="tenderToAssign.assignedAgentId"
        :loading="assigningAgent"
        @assign="handleAgentAssignment"
        @cancel="handleCancelAssignment"
      />
      <template #footer>
        <div class="flex justify-end gap-3">
          <BaseButton
            text="Cancel"
            variant="secondary"
            :disabled="assigningAgent"
            @click="handleCancelAssignment"
          />
          <BaseButton
            text="Assign Tender"
            variant="primary"
            :loading="assigningAgent"
            loading-text="Assigning..."
            @click="handleModalAssign"
          />
        </div>
      </template>
    </ModalDefault>
    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Watchlist Tender Details"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-2">
            <!-- Search Input -->
            <TextInputDefault
              v-model="searchFilter"
              placeholder="Search watchlist tenders..."
              id="searchwatchlisttenders"
              name="searchwatchlisttenders"
              type="text"
              className=""
              size="xss"
            />

            <div class="flex gap-2">
              <!-- Bulk Action Buttons -->
              <BaseButton
                v-if="hasSelectedTenders"
                size="xss"
                :icon="BookMarkIcon"
                variant="info"
                @click="openBulkRemoveModal"
                tooltip="Remove selected tenders from watchlist"
              />
              <BaseButton
                v-if="hasSelectedTenders"
                size="xss"
                :icon="AgentIcon"
                variant="primary"
                @click="openBulkAgentAssignmentModal"
                tooltip="Assign selected tenders to agent"
              />
              <!-- Refresh Button -->
              <BaseButton
                size="xss"
                :icon="RefreshIcon"
                @click="fetchWatchlistTenders"
                :loading="loading"
              />
              <!-- Clear Filter Button -->
              <BaseButton
                v-if="searchFilter"
                text="Clear Search"
                size="xss"
                variant="ghost"
                @click="clearFilters"
              />
            </div>
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i
            class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
          ></i>
          Loading watchlist tenders...
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">{{ error }}</p>
              <button
                @click="fetchWatchlistTenders"
                class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
              >
                Click here to retry
              </button>
            </div>
          </div>
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="tenders.length === 0"
          :icon="BookMarkIcon"
          title="No tenders in watchlist"
          description="Tenders you add to watchlist from the dashboard will appear here."
        />

        <!-- No Results after Filtering -->
        <EmptyState
          v-else-if="filteredTenders.length === 0"
          :icon="BookMarkIcon"
          title="No matching watchlist tenders"
          description="No watchlist tenders match your search criteria."
          action-text="Clear Filters"
          @action="clearFilters"
        />

        <!-- Tender Table -->
        <template v-else-if="filteredTenders.length > 0">
          <DataTable
            :value="filteredTenders"
            class="w-full"
            sortMode="multiple"
            scrollable
            scrollHeight="400px"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            :pt="{
              table: { style: 'border-collapse: separate; border-spacing: 0;' },
              thead: { style: 'position: sticky; top: 0; z-index: 20;' },
              tbody: { style: 'position: relative; z-index: 10;' },
            }"
          >
            <!-- Checkbox Column -->
            <Column
              headerStyle="width: 48px; position: sticky; left: 0; z-index: 30;"
              bodyStyle="position: sticky; left: 0; z-index: 25;"
              frozen
            >
              <template #header>
                <CheckBoxDefault
                  v-model="allSelected"
                  :disabled="filteredTenders.length === 0"
                />
              </template>
              <template #body="{ data }">
                <CheckBoxDefault
                  :modelValue="data.selected"
                  @update:modelValue="(val: boolean) => handleCheckboxChange(data, val)"
                />
              </template>
            </Column>

            <!-- Tender ID Column -->
            <Column
              sortable
              frozen
              field="referenceNo"
              header="Reference No"
              headerStyle="position: sticky; left: 48px; z-index: 30;"
              bodyStyle="position: sticky; left: 48px; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex flex-col max-w-[100px] text-xs">
                  <a
                    :href="data.tenderBriefLink"
                    target="_blank"
                    class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                    v-if="data.tenderBriefLink"
                  >
                    {{ data.referenceNo }}
                    <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                  </a>
                  <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                  <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
                </div>
              </template>
            </Column>

            <!-- Deadline Column -->
            <Column sortable field="deadline" header="Deadline">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <div class="text-xs">
                    {{ formatDate(data.deadline) }}
                  </div>
                  <div class="text-xs" :class="getExpiryStatus(data).class">
                    {{ getExpiryStatus(data).label }}
                  </div>
                </div>
              </template>
            </Column>

            <!-- Tender Type -->
            <Column sortable field="sheetType" header="Type">
              <template #body="{ data }">
                <Badge
                  :value="data.sheetType"
                  :class="'p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())"
                />
              </template>
            </Column>

            <!-- Status Column -->
            <Column sortable field="status" header="Status">
              <template #body="{ data }">
                <Badge
                  :value="getStatusLabel(data.status)"
                  :class="getStatusBadgeClass(data.status)"
                />
              </template>
            </Column>

            <!-- Added to Watchlist Column -->
            <Column sortable field="updatedAt" header="Added to Watchlist">
              <template #body="{ data }">
                <div class="text-xs">
                  {{ formatDate(data.updatedAt) }}
                </div>
              </template>
            </Column>

            <!-- Tender Brief Column -->
            <Column sortable field="tenderBrief" header="Description">
              <template #body="{ data }">
                <div
                  class="line-clamp-2 text-xs cursor-help"
                  @mouseenter="(event) => showTooltip(event, data.tenderBrief)"
                  @mouseleave="hideTooltip"
                >
                  {{ data.tenderBrief }}
                </div>
              </template>
            </Column>

            <!-- Organization Column -->
            <Column sortable field="organization" header="Organization">
              <template #body="{ data }">
                <div class="text-xs">{{ data.organization }}</div>
              </template>
            </Column>

            <!-- Location Column -->
            <Column sortable field="location" header="Location">
              <template #body="{ data }">
                <div class="flex items-center gap-2 text-xs">
                  {{ data.location }}
                </div>
              </template>
            </Column>

            <!-- Estimated Cost -->
            <Column sortable field="estimatedCost" header="Estimated Cost">
              <template #body="{ data }">
                <div class="font-medium">₹{{ data.estimatedCost?.toLocaleString('en-IN') }}</div>
              </template>
            </Column>

            <!-- Action Column -->
            <Column
              frozen
              field="action"
              header="Actions"
              headerStyle="width: 110px; position: sticky; right: 0; z-index: 30;"
              bodyStyle="position: sticky; right: 0; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex gap-2">
                  <!-- View button -->
                  <BaseButton
                    size="xss"
                    @click="viewTenderDetails(data)"
                    variant="secondary"
                    :icon="EyeIcon"
                    tooltip="View Tender Details"
                    tooltipPosition="bottom"
                    :disabled="hasSelectedTenders"
                  />
                  <!-- Assign to Agent button -->
                  <BaseButton
                    size="xss"
                    @click="openAgentAssignmentModal(data)"
                    variant="secondary"
                    :icon="AgentIcon"
                    tooltip="Assign to Agent"
                    tooltipPosition="bottom"
                    :disabled="hasSelectedTenders"
                  />
                  <!-- Remove from Watchlist button -->
                  <BaseButton
                    size="xss"
                    @click="openRemoveModal(data)"
                    variant="warning"
                    :icon="BookMarkIcon"
                    tooltip="Remove from Watchlist"
                    tooltipPosition="bottom"
                    :disabled="hasSelectedTenders"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} watchlist tender(s)
            <span v-if="expiredTendersCount > 0">
              ({{ expiredTendersCount }} expired)
            </span>
            <span v-if="hasSelectedTenders" class="text-blue-600 ml-2">
              ({{ selectedTenders.length }} selected)
            </span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>
